import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { CalendarClock, Check, Copy, ExternalLink, Eye, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  agentLabel,
  bodyOf,
  deriveTitle,
  fmtDate,
  fmtDateTime,
  isApproved,
  isPublished,
  isRejected,
  kindOf,
  originClasses,
  originOf,
  parseQa,
  platformLabel,
  qaShort,
  qaTone,
  stageClasses,
  stageLabel,
  stageOf,
  toLocalInputValue,
  tomorrowAtNine,
  toDate,
  typeLabel,
  wordCountOf,
  type StudioItem,
} from "./helpers";
import { errorBody, itemPlatformConnected, useStudioActions, useWorkspaceRole, wordpressConfigured } from "./use-studio";
import { plainPublishError, verifyOf, vetoStateOf } from "./agentic";
import { AgenticChips } from "./agentic-ui";
import { awaitingQaOf } from "./quality";
import { Chip } from "./chip";
import { WpPlanDialog, type WpPlanView } from "./wp-plan-dialog";

// ── Small pieces ────────────────────────────────────────────────────────────

export { Chip };

export const TYPE_CHIP = "border-border bg-muted/40 text-foreground/80";

export function StageChip({ item }: { item: StudioItem }) {
  const stage = stageOf(item);
  // A piece in its veto window (or held, or released) is described by its own chips, not by "Waiting for you".
  // (A rejected piece is held by the server on purpose, and still reads "Rejected".)
  if (["pending", "held", "released"].includes(vetoStateOf(item)) && !isPublished(item) && !isRejected(item)) return null;
  // Waiting on the independent reviewer is not waiting on the owner: its own chip says what the piece is waiting for.
  if (awaitingQaOf(item)) return null;
  // A post the follow-up check took down is not "Published", whatever its publish date says.
  const verify = verifyOf(item);
  if (verify?.state === "pulled_back") {
    return (
      <Chip className="border-red-500/40 bg-red-500/15 text-red-200" title={verify.reason ?? "The check after publishing found a problem, so it was taken down."}>
        Pulled back
      </Chip>
    );
  }
  return <Chip className={stageClasses(stage)}>{stageLabel(item, stage)}</Chip>;
}

export function QaChip({ item }: { item: StudioItem }) {
  const qa = parseQa(item);
  if (!qa) return null;
  return <Chip className={qaTone(qa)}>{qaShort(qa)}</Chip>;
}

/** "Claude team" / "Gemini" / "You": who produced the item. */
export function OriginChip({ item }: { item: StudioItem }) {
  const origin = originOf(item);
  return (
    <Chip className={originClasses(origin.id)} title={origin.title}>
      <span data-testid={`origin-${origin.id}`}>{origin.label}</span>
    </Chip>
  );
}

export function SectionHeading({ title, count, hint }: { title: string; count?: number; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {typeof count === "number" && <span className="text-sm text-muted-foreground">{count}</span>}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center" data-testid="empty-state">
      <p className="text-base font-semibold text-foreground">{title}</p>
      {children && <div className="mt-2 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

// ── List row ────────────────────────────────────────────────────────────────

/** One item in any list. The whole text area opens the item; `actions` sit beside it, never inside it. */
export function ItemRow({
  item,
  onOpen,
  actions,
  showStage = true,
  dateLabel,
  note,
}: {
  item: StudioItem;
  onOpen: (item: StudioItem) => void;
  actions?: ReactNode;
  showStage?: boolean;
  /** Override the date shown (e.g. the scheduled time in calendar lists). */
  dateLabel?: string;
  /** A line of explanation under the meta line (why this needs attention). */
  note?: ReactNode;
}) {
  const title = deriveTitle(item);
  const words = wordCountOf(item);
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border bg-card/60 p-3 transition-colors hover:border-blue-500/40 sm:flex-row sm:items-center sm:p-4"
      data-testid={`item-row-${item.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60"
        data-testid={`item-open-${item.id}`}
      >
        <div className="flex gap-3">
        {item.mediaUrl && kindOf(item) === "social" && (
          <img src={item.mediaUrl} alt="" loading="lazy" className="h-16 w-16 shrink-0 rounded-lg border border-border bg-muted object-cover" data-testid={`item-thumb-${item.id}`} />
        )}
        <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <Chip className={TYPE_CHIP}>{typeLabel(item)}</Chip>
          <OriginChip item={item} />
          <QaChip item={item} />
          {showStage && <StageChip item={item} />}
          <AgenticChips item={item} />
        </div>
        <p className="line-clamp-2 break-words text-sm font-semibold text-foreground sm:text-[15px]">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {agentLabel(item)}
          <span aria-hidden> · </span>
          {dateLabel ?? fmtDate(item.createdAt)}
          {words > 0 && (
            <>
              <span aria-hidden> · </span>
              {words.toLocaleString()} words
            </>
          )}
        </p>
        {note && <p className="mt-1 text-xs text-amber-200/90" data-testid={`item-note-${item.id}`}>{note}</p>}
        </div>
        </div>
      </button>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
  );
}

// ── Publish / schedule ──────────────────────────────────────────────────────

/**
 * True when the body is a finished, pre-assembled HTML article. This mirrors the server's own test: such articles
 * are the ones the publish route can preview (dry run) instead of posting. Older articles take the legacy publisher,
 * which has no preview, so no preview is offered for them.
 */
export function isAssembledHtml(item: StudioItem): boolean {
  const doc = item.documentContent || "";
  return /application\/ld\+json/i.test(doc) || /^\s*(<!doctype|<article)\b/i.test(doc);
}

function CopyTextButton({ item, size = "sm" }: { item: StudioItem; size?: "sm" | "default" }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(bodyOf(item));
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          /* clipboard blocked: nothing else to do */
        }
      }}
      data-testid={`button-copy-${item.id}`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied" : "Copy text"}
    </Button>
  );
}

/**
 * What can be done with an approved item, in plain words:
 * an article gets "Publish to WordPress"; a social post gets "Schedule".
 * Anything that stops it working is written out underneath (not connected, failed last time).
 * Documents (plans, briefs, emails, ads) have no publish step.
 */
export function PublishActions({
  item,
  workspace,
  compact = false,
  onDone,
}: {
  item: StudioItem;
  workspace: any;
  compact?: boolean;
  onDone?: () => void;
}) {
  const { schedule, publishWordPress } = useStudioActions();
  const { canPublishLive, isLoading: roleLoading } = useWorkspaceRole();
  const [planView, setPlanView] = useState<WpPlanView | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [when, setWhen] = useState(() => {
    const existing = toDate(item.scheduledAt);
    return existing && existing.getTime() > Date.now() ? toLocalInputValue(existing) : tomorrowAtNine();
  });
  const [confirmWp, setConfirmWp] = useState(false);

  const kind = kindOf(item);
  if (!isApproved(item) || isPublished(item)) return null;

  // In the Review view the status panel above already explains a failed publish; lists show it here.
  const failed = compact ? plainPublishError(item, workspace) : null;

  if (kind === "article") {
    const wpOk = wordpressConfigured(workspace);
    // Only the owner or an admin can publish live; the server turns anyone else's request into a preview.
    const liveBlocked = canPublishLive === false;
    // A preview is only safe to ask for when the server will treat it as one (a finished HTML article).
    const previewable = isAssembledHtml(item);
    const busy = publishWordPress.isPending;
    const previewing = busy && publishWordPress.variables?.dryRun === true;
    const openPlan = (view: WpPlanView) => {
      setPlanView(view);
      setPlanOpen(true);
    };
    const run = (dryRun: boolean) =>
      publishWordPress.mutate(
        { id: item.id, dryRun },
        {
          onSuccess: (data) => {
            if (data?.dryRun === true) openPlan({ result: data });
            else onDone?.();
          },
          onError: (e) => {
            const body = errorBody(e);
            if (body?.blocked) openPlan({ result: body, blocked: true });
          },
        },
      );
    const publishButton = (
      <Button
        type="button"
        size={compact ? "sm" : "default"}
        disabled={!wpOk || busy || liveBlocked || roleLoading}
        onClick={() => setConfirmWp(true)}
        data-testid={`button-publish-wp-${item.id}`}
      >
        {busy && !previewing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        Publish to WordPress
      </Button>
    );
    return (
      <div className="flex flex-col gap-2" data-testid={`publish-actions-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          {liveBlocked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60" data-testid={`publish-disabled-${item.id}`}>
                  {publishButton}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">Only the workspace owner or an admin can publish to the website.</TooltipContent>
            </Tooltip>
          ) : (
            publishButton
          )}
          {previewable && (
            <Button
              type="button"
              variant="outline"
              size={compact ? "sm" : "default"}
              disabled={busy}
              onClick={() => run(true)}
              data-testid={`button-preview-wp-${item.id}`}
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Preview what will be posted
            </Button>
          )}
        </div>
        {liveBlocked && (
          <p className="text-xs text-muted-foreground" data-testid="note-owner-only">
            Only the workspace owner or an admin can publish to the website.
            {previewable ? " You can still preview what would be posted." : ""}
          </p>
        )}
        <WpPlanDialog open={planOpen} onOpenChange={setPlanOpen} view={planView} articleTitle={deriveTitle(item)} />
        {!wpOk && (
          <p className="text-xs text-amber-300" data-testid="blocker-wordpress">
            WordPress isn't connected, so this can't be published from here.{" "}
            <Link href="/settings" className="underline underline-offset-2">Connect it in Settings</Link>.
          </p>
        )}
        {failed && (
          <p className="text-xs text-red-300" data-testid={`publish-error-${item.id}`} title={failed.raw}>
            {failed.text}
          </p>
        )}
        <AlertDialog open={confirmWp} onOpenChange={setConfirmWp}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish this article to your website?</AlertDialogTitle>
              <AlertDialogDescription>
                "{deriveTitle(item)}" goes live on WordPress right away. This can't be undone from here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Not yet</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => run(false)}
                data-testid={`button-confirm-publish-${item.id}`}
              >
                Publish now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (kind === "social") {
    const connected = itemPlatformConnected(workspace, item);
    const scheduledAt = toDate(item.scheduledAt);
    const label = scheduledAt ? "Change time" : "Schedule";
    const valid = !!when && !Number.isNaN(new Date(when).getTime());
    return (
      <div className="flex flex-col gap-2" data-testid={`publish-actions-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size={compact ? "sm" : "default"}
            variant={picking ? "outline" : "default"}
            onClick={() => setPicking((v) => !v)}
            data-testid={`button-schedule-${item.id}`}
          >
            <CalendarClock size={14} />
            {label}
          </Button>
          <CopyTextButton item={item} size={compact ? "sm" : "default"} />
        </div>
        {picking && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/30 p-3">
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Date and time
              <input
                type="datetime-local"
                value={when}
                min={toLocalInputValue(new Date())}
                onChange={(e) => setWhen(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                data-testid={`input-schedule-${item.id}`}
              />
            </label>
            <Button
              type="button"
              size="sm"
              disabled={!valid || schedule.isPending}
              onClick={() =>
                schedule.mutate(
                  { id: item.id, when: new Date(when) },
                  {
                    onSuccess: () => {
                      setPicking(false);
                      onDone?.();
                    },
                  },
                )
              }
              data-testid={`button-confirm-schedule-${item.id}`}
            >
              {schedule.isPending && <Loader2 size={14} className="animate-spin" />}
              Confirm
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPicking(false)}>
              Cancel
            </Button>
          </div>
        )}
        {!compact && scheduledAt && !picking && (
          <p className="text-xs text-muted-foreground">Planned for {fmtDateTime(scheduledAt)}.</p>
        )}
        {!compact && !connected && (
          <p className="text-xs text-amber-300" data-testid="blocker-social">
            {platformLabel(item)} isn't connected, so this won't post by itself. Copy the text and post it by hand, or{" "}
            <Link href="/settings" className="underline underline-offset-2">connect {platformLabel(item)} in Settings</Link>.
          </p>
        )}
        {failed && (
          <p className="text-xs text-red-300" data-testid={`publish-error-${item.id}`} title={failed.raw}>
            {failed.text}
          </p>
        )}
      </div>
    );
  }

  return null;
}

export function LiveLink({ item }: { item: StudioItem }) {
  if (!item.blogUrl) return null;
  return (
    <a
      href={item.blogUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-blue-400 underline-offset-2 hover:underline"
      data-testid="link-live-post"
    >
      View live <ExternalLink size={13} />
    </a>
  );
}
